import { buildUniqueSelector } from './selector-utils';

export class HeadingOrderRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'headingOrder';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode) {
		const presentationalHeadings = this.findPresentationalHeadings(documentNode);
		const headingNodes = Array.from(documentNode.querySelectorAll('h1, h2, h3, h4, h5, h6')).filter(
			(headingNode) => !headingNode.closest('#wpadminbar')
		);

		const headings = headingNodes.map((headingNode) => {
			const tagName = headingNode.tagName.toLowerCase();
			const level = Number.parseInt(tagName.replace('h', ''), 10);

			return {
				level,
				tag: tagName,
				text: (headingNode.textContent || '').trim(),
				selector: buildUniqueSelector(headingNode)
			};
		});

		const issues = [];
		const failedItems = [];
		const h1Count = headings.filter((heading) => heading.level === 1).length;

		if (headings.length === 0) {
			issues.push(this.__( 'No semantic headings found.', 'a11yfix' ));
		}

		if (headings.length > 0 && headings[0].level !== 1) {
			issues.push(this.__( 'Out-of-order: first heading is not H1.', 'a11yfix' ));
			failedItems.push({
				type: 'firstHeadingNotH1',
				selector: headings[0].selector,
				tag: headings[0].tag,
				text: headings[0].text
			});
		}

		for (let index = 1; index < headings.length; index += 1) {
			const previous = headings[index - 1];
			const current = headings[index];

			if (current.level > previous.level + 1) {
				issues.push(
					`${this.__( 'Skipped level:', 'a11yfix' )} H${previous.level} → H${current.level}`
				);
				failedItems.push({
					type: 'skippedLevel',
					selector: current.selector,
					tag: current.tag,
					text: current.text
				});
			}

			if (current.level > 1 && !headings.slice(0, index).some((heading) => heading.level === current.level - 1)) {
				issues.push(
					`${this.__( 'Out-of-order sequence near', 'a11yfix' )} H${current.level}`
				);
				failedItems.push({
					type: 'outOfOrderSequence',
					selector: current.selector,
					tag: current.tag,
					text: current.text
				});
			}
		}

		if (h1Count > 1) {
			issues.push(`${this.__( 'Multiple H1 headings found:', 'a11yfix' )} ${h1Count}`);
			for (const heading of headings.filter((item) => item.level === 1)) {
				failedItems.push({
					type: 'multipleH1',
					selector: heading.selector,
					tag: heading.tag,
					text: heading.text
				});
			}
		}

		if (presentationalHeadings.length > 0) {
			issues.push(
				`${this.__( 'Presentational headings found (non-semantic):', 'a11yfix' )} ${presentationalHeadings.length}`
			);

			for (const presentationalHeading of presentationalHeadings) {
				failedItems.push({
					type: 'presentationalHeading',
					selector: presentationalHeading.selector,
					tag: presentationalHeading.tag,
					text: presentationalHeading.text
				});
			}
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Heading order issues found:', 'a11yfix' )} ${issues.length}`,
				details: {
					headings,
					issues,
					presentationalHeadings,
					failedItems
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'Heading order looks good.', 'a11yfix' ),
			details: {
				headings,
				issues: [],
				presentationalHeadings: [],
				failedItems: []
			}
		};
	}

	findPresentationalHeadings(documentNode) {
		const candidates = Array.from(documentNode.querySelectorAll('div, span, p, strong, b')).filter(
			(node) => !node.closest('#wpadminbar')
		);

		return candidates
			.filter((node) => {
				if (node.closest('h1, h2, h3, h4, h5, h6')) {
					return false;
				}

				const text = (node.textContent || '').trim();

				if (text.length < 4 || text.length > 120) {
					return false;
				}

				const fontSizePx = this.getComputedFontSizePx(node);

				if (fontSizePx === null) {
					return false;
				}

				return fontSizePx >= 24;
			})
			.map((node) => ({
				tag: node.tagName.toLowerCase(),
				text: (node.textContent || '').trim().slice(0, 120),
				selector: buildUniqueSelector(node)
			}));
	}

	getComputedFontSizePx(node) {
		const defaultView = node?.ownerDocument?.defaultView || window;
		const computedStyle = defaultView.getComputedStyle(node);
		const fontSizeValue = Number.parseFloat(computedStyle.fontSize || '');

		if (Number.isNaN(fontSizeValue)) {
			return null;
		}

		return fontSizeValue;
	}
}
